import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";
import { GridFilterModel } from "@mui/x-data-grid-pro";

export const tagAPI = createApi({
    reducerPath: 'tagAPI',
    tagTypes: ['Tags', 'TagsList', 'TagsListBySearchTerm', 'SearchTermsByTags'],
    baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL + '/tags-manager' }),
    endpoints: (builder) => ({
        getTags: builder.query<any, {
            workspaceId: number,
            start: string,
            end: string,
            keywords: string[],
            countries: string[],
            devices: string[],
            platforms: string[],
            page: number,
            pageSize: number,
            filterModel?: GridFilterModel,
            tags?: string[],
            sortField?: string,
            sortOrder?: string,
        }>({
            query: ({ workspaceId, start, end, keywords, countries, devices, platforms, page, pageSize, filterModel, tags, sortField, sortOrder }) => ({
                url: `/search-terms?workspace_id=${workspaceId}`,
                method: filterModel && (filterModel?.items.length > 0) ? "POST" : "GET",
                params: {
                    start,
                    end,
                    keywords,
                    countries,
                    devices,
                    platforms,
                    page,
                    pageSize,
                    tags,
                    sortField,
                    sortOrder,
                },
                body: filterModel && (filterModel?.items.length > 0) ? filterModel : undefined,
            }),
            providesTags: ['Tags'],
        }),
        getTagsList: builder.query<any, {
            workspaceId: number,
        }>({
            query: ({ workspaceId }) => ({
                url: `/?workspace_id=${workspaceId}`,
            }),
            providesTags: ['TagsList'],
        }),
        getTagsForSearchTerm: builder.query<any, {
            workspaceId: number,
            searchTerm: string,
        }>({
            query: ({ workspaceId, searchTerm }) => ({
                url: `/search-term/tags?workspace_id=${workspaceId}&search_term=${searchTerm}`,
            }),
            providesTags: ['TagsListBySearchTerm'],
        }),
        tagSearchTerm: builder.mutation<any, {
            workspaceId: number,
            searchTerm: string,
            tags: { label: string }[],
        }>({
            query: ({ workspaceId, searchTerm, tags }) => ({
                url: `/search-term/tag/bulk?workspace_id=${workspaceId}`,
                method: "POST",
                body: { tags, search_term: searchTerm },
            }),
            invalidatesTags: ['TagsListBySearchTerm', 'TagsList', 'Tags', 'SearchTermsByTags'],
        }),
        tagSearchTermsWithTagsBulk: builder.mutation<any, {
            workspaceId: number,
            addTags: { label: string }[],
            searchTerms?: { name: string }[],
            start?: string,
            end?: string,
            keywords?: string[],
            countries?: string[],
            devices?: string[],
            platforms?: string[],
            tags?: string[],
            filterModel?: GridFilterModel,
        }>({
            query: ({ workspaceId, tags, searchTerms, start, end, keywords, countries, devices, platforms, filterModel, addTags }) => ({
                url: `/search-terms/tag/bulk?workspace_id=${workspaceId}`,
                method: "POST",
                params: {
                    start,
                    end,
                    keywords,
                    countries,
                    devices,
                    platforms,
                    tags,
                },
                body: { tags: addTags, search_terms: searchTerms, filter_model: filterModel },
            }),
            invalidatesTags: ['TagsListBySearchTerm', 'TagsList', 'Tags', 'SearchTermsByTags'],

        }),
        untagSearchTerm: builder.mutation<any, {
            workspaceId: number,
            searchTerm: string,
            tag: string,
        }>({
            query: ({ workspaceId, searchTerm, tag }) => ({
                url: `/search-term/untag?workspace_id=${workspaceId}`,
                method: "PUT",
                body: { search_term: searchTerm, name: tag },
            }),
            invalidatesTags: ['TagsListBySearchTerm', 'TagsList', 'Tags', 'SearchTermsByTags'],
        }),
        getSearchTermsByTags: builder.query<{ search_terms: { id: number, name: string }[] }, {
            workspaceId: number,
            tags: string[],
        }>({
            query: ({ workspaceId, tags }) => ({
                url: `/search-terms-by-tags`,
                // `params` gets URL-encoded: cluster names may contain reserved characters (e.g. "coupons & promo")
                params: { workspace_id: workspaceId, tags: tags.join(",") },
            }),
            providesTags: ['SearchTermsByTags'],
        }),
        getFilterdSummary: builder.query<any, {
            workspaceId: number,
            start: string,
            end: string,
            keywords: string[],
            countries: string[],
            devices: string[],
            platforms: string[],
            page: number,
            pageSize: number,
            filterModel?: GridFilterModel,
            tags?: string[],
            sortField?: string,
            sortOrder?: string,
        }>({
            query: ({ workspaceId, start, end, keywords, countries, devices, platforms, page, pageSize, filterModel, tags, sortField, sortOrder }) => ({
                url: `/filterd-summary?workspace_id=${workspaceId}`,
                method: filterModel && (filterModel?.items.length > 0) ? "POST" : "GET",
                params: {
                    start,
                    end,
                    keywords,
                    countries,
                    devices,
                    platforms,
                    page,
                    pageSize,
                    tags,
                    sortField,
                    sortOrder,
                },
                body: filterModel && (filterModel?.items.length > 0) ? filterModel : undefined,
            }),
            providesTags: ['Tags'],
        }),
        bulkRemoveClusters: builder.mutation<any, {
            workspaceId: number,
            tags: string[],
            searchTerms: string[],
        }>({
            query: ({ workspaceId, tags, searchTerms }) => ({
                url: `/search-terms/untag/bulk?workspace_id=${workspaceId}`,
                method: "PUT",
                body: {
                    tags,
                    search_terms: searchTerms,
                },
            }),
            invalidatesTags: [
                'TagsListBySearchTerm',
                'TagsList',
                'Tags',
                'SearchTermsByTags'
            ],
        }),
    }),
});


export const {
    useGetTagsQuery,
    useGetTagsListQuery,
    useGetTagsForSearchTermQuery,
    useTagSearchTermMutation,
    useTagSearchTermsWithTagsBulkMutation,
    useUntagSearchTermMutation,
    useGetSearchTermsByTagsQuery,
    useGetFilterdSummaryQuery,
    useBulkRemoveClustersMutation
} = tagAPI;
export default tagAPI;